// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Select, Text, Loader, Skeleton } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import { openModal } from '@mantine/modals';
import { IconChevronDown } from '@tabler/icons';
import AvailabilityIcon from '../../Atoms/AvailabilityIcon';
import PanePaper from '../../Molecules/PanePaper';
import { EventContext } from '../../Service/Context/EventContext';
import { DataS } from '../../Service/DataS';
import BookingEvent from '../../Types/Event';
import BookingState from '../../Types/Enums/BookingState';
import { EventS } from '../../Service/EventS';
import { CustomEventState } from '../../Types/EventT';
import Dot from '../../Atoms/Dot';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import { LicenceS } from '../../Service/LicenceS';
import EventKindSelect from '../../Molecules/Event/EventKindSelect';
import LeadOriginSelect from '../../Molecules/Event/LeadOriginSelect';
import ChangeRecommendedEventStateModal from './ChangeRecommendedEventStateModal';
import AvailabilityCheckV2 from '../../Molecules/AvailableCheckNew';
import BookitupTextInput from '../../Atoms/inputs/BookitupTextInput';

const AvailabilityPane = () => {
  const { event, saveEvent, loadingEvent } = useContext<any>(EventContext);
  const { recommendationId, bookingState, name } = (event as BookingEvent) ?? {};
  const { licence } = useJwtClaims();
  const customStatesAvailable = LicenceS.hasSuitableLicence('INTERACTIVE', licence);

  const changeBookingState = (_bookingState: BookingState) => {
    if (recommendationId) {
      const currentState = i18n._(`recommendation.bookingState.${bookingState.toLowerCase()}`);
      const newState = i18n._(`recommendation.bookingState.${_bookingState.toLowerCase()}`);
      openModal({
        title: (
          <Text mt="md" size="xl" sx={{ lineHeight: 1.2 }}>
            Statusänderung von{' '}
            <Text span weight="bolder">
              {currentState}
            </Text>{' '}
            auf{' '}
            <Text span weight="bolder">
              {newState}
            </Text>
          </Text>
        ),
        closeOnEscape: true,
        transition: 'slide-down',
        children: (
          <ChangeRecommendedEventStateModal
            recommendationId={recommendationId}
            newBookingState={_bookingState}
            onFinish={() => saveEvent({ bookingState: _bookingState })}
          />
        ),
      });
    } else {
      saveEvent({ bookingState: _bookingState });
    }
  };

  return (
    <PanePaper title="Allgemein">
      {loadingEvent && <Skeleton height={180} />}
      {!loadingEvent && event && (
        <>
          <AvailabilityCheckV2 />
          {customStatesAvailable && <CustomStateSelect />}
          {!customStatesAvailable && (
            <Select
              mb="sm"
              label="Event-Status"
              value={event.bookingState}
              data={DataS.getBookingStateOptions(recommendationId)}
              onChange={(state) => changeBookingState(state as BookingState)}
              icon={<AvailabilityIcon bookingState={event.bookingState} />}
            />
          )}
          {loadingEvent && <Skeleton height={32} width="100%" />}
          {event && (
            <BookitupTextInput
              mb="sm"
              label="Event-Name"
              defaultValue={name ?? ''}
              onBlurSave={(val) => saveEvent({ name: val })}
            />
          )}
          <EventKindSelect />
          <LeadOriginSelect />
        </>
      )}
    </PanePaper>
  );
};

const CustomStateSelect = () => {
  const { event, saveEvent } = useContext<any>(EventContext);
  const [eventStates, setEventStates] = useState<CustomEventState[]>([]);
  const [loading, setLoading] = useState(true);
  const { recommendationId, bookingState } = event ?? {};
  const data = DataS.getEventStateOptions(eventStates, recommendationId !== null);
  const dotColor = event ? EventS.getStateColor(event) : '';
  const value = event ? EventS.getEventStateValue(event, eventStates) : '';

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    setLoading(true);
    const _eventStates = await EventS.fetchEventStates();
    setEventStates(EventS.sortCustomStates(_eventStates));
    setLoading(false);
  };

  const onChange = async (value: string) => {
    const selectedState = eventStates.find((s) => s.label === value);
    if (selectedState) {
      if (recommendationId) {
        const currentState = i18n._(`recommendation.bookingState.${bookingState.toLowerCase()}`);
        const newState = selectedState.id
          ? selectedState.label
          : i18n._(`recommendation.bookingState.${selectedState.originState.toLowerCase()}`);
        openModal({
          title: (
            <Text mt="md" size="xl" sx={{ lineHeight: 1.2 }}>
              Statusänderung von{' '}
              <Text span weight="bolder">
                {currentState}
              </Text>{' '}
              auf{' '}
              <Text span weight="bolder">
                {newState}
              </Text>
            </Text>
          ),
          closeOnEscape: true,
          transition: 'slide-down',
          children: (
            <ChangeRecommendedEventStateModal
              recommendationId={recommendationId}
              newBookingState={selectedState.originState}
              onFinish={() => {
                const patch = selectedState.id
                  ? { eventStateId: selectedState.id }
                  : { bookingState: selectedState.originState };
                saveEvent(patch);
              }}
            />
          ),
        });
      } else {
        const { id, originState } = selectedState;
        const patch = id ? { eventStateId: id } : { bookingState: originState };
        setLoading(true);
        await saveEvent(patch);
        setLoading(false);
      }
    }
  };

  return (
    <Select
      mb="sm"
      label="Event status"
      disabled={loading}
      value={value}
      data={data}
      icon={loading ? <Loader size="xs" /> : <Dot withoutShadow colour={dotColor} />}
      onChange={(val) => val && onChange(val)}
      styles={{ rightSection: { pointerEvents: 'none' } }}
      rightSection={<IconChevronDown size={14} />}
      rightSectionWidth={25}
    />
  );
};

export default AvailabilityPane;
